/**
 * GlobeHeader - Minimal header overlay for the globe page.
 *
 * Contains YUNACABA logo, account avatar, and optional navigation.
 * Positioned at the top of the screen over the globe.
 */

import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { AccountAvatar } from '@/components/controls/AccountAvatar'
import { Button } from '@/components/ui/button'
import { FONT_STYLES } from '@/styles/fonts'

interface GlobeHeaderProps {
  /** Whether to show the back button */
  showBackButton?: boolean
  /** Callback when back button is clicked */
  onBack?: () => void
}

/**
 * Minimal header overlay for the globe page.
 *
 * @example
 * ```tsx
 * <main className="relative h-screen">
 *   <GlobeCanvas />
 *   <GlobeHeader />
 * </main>
 * ```
 */
export function GlobeHeader({ showBackButton = false, onBack }: GlobeHeaderProps) {
  const navigate = useNavigate()

  return (
    <>
      {/* Left header - Back button + Logo */}
      <div className="absolute top-4 left-4 z-40 flex items-center gap-3">
        {/* Back button */}
        {showBackButton && (
          <button
            onClick={onBack}
            className="p-1.5 bg-black/50 backdrop-blur-md rounded-full border border-white/20 text-white/70 hover:text-white hover:bg-black/70 transition-colors"
            aria-label="Close trip"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          </button>
        )}

        {/* YUNACABA Logo */}
        <h1
          className="text-white text-5xl font-bold tracking-[0.2em] opacity-90"
          style={{
            ...FONT_STYLES.pioneer,
            textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
          }}
        >
          YUNACABA
        </h1>
      </div>

      {/* Right side header */}
      <header className="absolute top-0 right-0 p-4 z-40 flex items-center gap-3">
        {/* Debug button (hidden in production) */}
        {import.meta.env.DEV && (
          <Button
            onClick={() => navigate('/debug/globe')}
            variant="ghost"
            size="sm"
            className="text-xs text-white/50 hover:text-white/80 hover:bg-white/10"
          >
            Debug
          </Button>
        )}

        {/* Account avatar */}
        <AccountAvatar size={32} className="shadow-lg" />
      </header>
    </>
  )
}
